<template>
    <VDialog :open="modelValue" headline="View Scopes" modal @close="close()" style="--dialog-min-inline-size: 500px; --dialog-max-block-size: 80vh;">
        <template #body>
            <div class="modal-card">
                <div v-if="loading" class="spinner">
                    <VProgressRing connotation="cta" />
                </div>
                <div v-else-if="error" class="error-wrap">
                    <strong>Failed to load scopes</strong>
                    <div class="muted">{{ error }}</div>
                    <VButton appearance="outlined" connotation="cta" label="Retry" size="condensed" @click="fetchAndGroup" />
                </div>
                <div v-else>
                    <div v-for="sec in sections" :key="sec.key" class="section">
                        <h3 class="section-title">{{ sec.title }}</h3>
                        <div class="pills">
                            <span v-for="s in sec.items" :key="s.id" class="scope-pill">{{ s.scope }}</span>
                        </div>
                    </div>
                    <div v-if="!sections.length" class="empty-state">
                        <VEmptyState icon="search-line" headline="No scopes found for this webhook." />
                    </div>
                </div>
            </div>
        </template>
    </VDialog>
</template>

<script>
import { useUIStore } from '@/stores/ui';

export default {
    name: "ViewScopeModal",
    props: {
        modelValue: { type: Boolean, required: true },
        webhookId: { type: String, default: null },
        viewWebhookScopesWithId: { type: Function, required: true },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            loading: false,
            error: null,
            sections: [],
        };
    },
    methods: {
        close() {
            this.$emit("update:modelValue", false);
            this.reset();
        },
        reset() {
            this.loading = false;
            this.error = null;
            this.sections = [];
        },
        prettyTitle(scopeName) {
            if (!scopeName) return "";
            const cap = scopeName.charAt(0).toUpperCase() + scopeName.slice(1);
            return cap.endsWith("s") ? cap : `${cap}s`;
        },
        groupByNamespace(scopes) {
            const bucket = new Map();
            for (const s of scopes) {
                const scopeName = (s.scope || "").split(":")[0] || "other";
                if (!bucket.has(scopeName)) bucket.set(scopeName, []);
                bucket.get(scopeName).push({ id: s.id, scope: s.scope });
            }
            return Array.from(bucket.entries()).map(([scopeName, items]) => ({
                key: scopeName,
                title: this.prettyTitle(scopeName),
                items,
            }));
        },
        async fetchAndGroup() {
            try {
                this.loading = true;
                this.error = null;
                const resp = await this.viewWebhookScopesWithId(this.webhookId);
                const list = Array.isArray(resp) ? resp : resp?.scopes ?? [];
                this.sections = this.groupByNamespace(list);
            } catch (e) {
                useUIStore().updateToast({
                    open: true,
                    message: e?.message || "Failed to load scopes",
                    type: "danger",
                });
            } finally {
                this.loading = false;
            }
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            async handler(val) {
                if (!val) return;
                await this.fetchAndGroup();
            },
        },
    },
};
</script>

<style scoped lang="scss">
.modal-card {
    min-height: 200px;
}
.spinner,
.empty-state,
.error-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    gap: 12px;
    flex-direction: column;
}
.section {
    margin-bottom: 18px;
}
.section-title {
    margin: 0 0 20px;
    font-size: 18px;
    font-weight: 400;
    color: #111827;
}
.pills {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    flex-direction: column;
}
.scope-pill {
    display: inline-block;
    font-size: 14px;
    color: #cf3c4f;
    background: var(--background-primary);
    border-radius: 6px;
    padding: 6px 10px;
    line-height: 1;
    width: fit-content;
}
.muted {
    color: var(--gray-04);
}
</style>
